import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Column,
  ColumnInstance,
  HeaderGroup,
  TableInstance,
  useFilters,
  useGlobalFilter,
  useSortBy,
  UseSortByColumnProps,
  useTable,
} from 'react-table';
import './TaskDataTable.scss';
import { DefaultColumnFilter } from './helpers/DefaultColumnFilter';
import { FixedSizeList } from 'react-window';
import { TaskHistoryItem } from '../tasks-list/TaskHistoryModel';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import { formatDate } from '../../utilities/dateHelpers';

interface TaskHistoryTableProps {
  taskHistoryItems: TaskHistoryItem[];
  onDataReady?: (rows: any[]) => void;
}

const TaskHistoryTable: React.FC<TaskHistoryTableProps> = ({ taskHistoryItems, onDataReady }) => {
  // Use a stable reference for data to prevent unnecessary re-renders
  const [stableData, setStableData] = useState(taskHistoryItems);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const [containerWidth, setContainerWidth] = useState(1200); // Default width
  const currentFiltersRef = useRef(currentFilters);
  const listRef = useRef<FixedSizeList>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update stable data when taskHistoryItems prop changes
  useEffect(() => {
    setStableData(taskHistoryItems);
  }, [taskHistoryItems]);

  // Measure container width for responsive table
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const data = useMemo(() => stableData, [stableData]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef);

  // Function to generate consistent colors for values
  const getColorForValue = (value: string): string => {
    if (!value) return '#6b7280'; // gray for empty values

    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      const char = value.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    const colors = [
      '#60a5fa',
      '#34d399',
      '#fbbf24',
      '#f87171',
      '#a78bfa',
      '#22d3ee',
      '#a3e635',
      '#fb923c',
      '#f472b6',
      '#818cf8',
      '#2dd4bf',
      '#fde047',
      '#fb7185',
      '#fdba74',
      '#c084fc',
      '#67e8f9',
      '#bef264',
      '#fca5a5',
      '#ddd6fe',
      '#93c5fd',
      '#6ee7b7',
      '#fef08a',
      '#fda4af',
      '#fed7aa',
      '#e9d5ff',
      '#a5f3fc',
      '#d9f99d',
      '#fecaca',
      '#f3e8ff',
      '#dbeafe',
      '#86efac',
      '#fef3c7',
      '#fecdd3',
      '#fef0e5',
      '#f5f3ff',
      '#ecfeff',
      '#f0fdf4',
      '#fef7ed',
      '#fdf2f8',
      '#eff6ff',
      '#5eead4',
      '#fde68a',
      '#f9a8d4',
      '#fed7d7',
      '#c7d2fe',
      '#a5b4fc',
      '#94a3b8',
      '#fbbf24',
      '#ec4899',
      '#8b5cf6',
      '#06b6d4',
      '#84cc16',
      '#f97316',
      '#ef4444',
      '#3b82f6',
      '#10b981',
      '#f59e0b',
      '#6366f1',
      '#14b8a6',
      '#eab308',
      '#7dd3fc',
      '#4ade80',
      '#facc15',
      '#ff6b6b',
      '#9f7aea',
      '#38bdf8',
      '#65a30d',
      '#ff8c42',
      '#ff69b4',
      '#4f46e5',
      '#20e3b2',
      '#ffdd59',
      '#ff8a80',
      '#ffab40',
      '#b388ff',
      '#64b5f6',
      '#81c784',
      '#ffcc02',
      '#f48fb1',
      '#9575cd',
      '#4dd0e1',
      '#aed581',
      '#ffd54f',
      '#e57373',
      '#ba68c8',
      '#4fc3f7',
      '#9ccc65',
      '#ffb74d',
      '#f06292',
      '#7986cb',
      '#26c6da',
      '#c5e1a5',
      '#fff176',
      '#ff8a65',
      '#ce93d8',
      '#42a5f5',
      '#dce775',
      '#ffab91',
      '#f8bbd9',
      '#90caf9',
    ];

    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };

  const columns: Column<TaskHistoryItem>[] = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'noteDate' as const,
        filter: multiConditionFilter,
        Cell: ({ value }: { value: string }) => <span>{formatDate(value, true)}</span>,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const dateA = new Date(rowA.values[columnId]);
          const dateB = new Date(rowB.values[columnId]);

          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return 0;
        },
      },
      {
        Header: 'Event',
        accessor: 'noteDescription',
        filter: multiConditionFilter,
      },
      {
        Header: 'Type',
        accessor: 'noteSource',
        disableFilters: true,
        Cell: ({ value }: { value: string }) => {
          const description = value || '';
          const color = getColorForValue(description);

          return (
            <span
              style={{
                color: color,
                fontWeight: '600',
                border: `1px solid ${color}`,
                borderRadius: '4px',
                padding: '3px 6px',
              }}
            >
              {description}
            </span>
          );
        },
      },
      {
        Header: 'User id',
        accessor: 'userId',
        filter: multiConditionFilter,
      },
    ],
    [],
  );

  const defaultColumn: any = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      defaultColumn: defaultColumn,
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetRowState: false,
      getRowId: (_row: TaskHistoryItem, index: number) => `task-${index}`, // Stable row IDs
    }),
    [columns, data, defaultColumn],
  );

  const instance = useTable<TaskHistoryItem>(tableConfig, useGlobalFilter, useFilters, useSortBy);

  const { getTableProps, headerGroups, rows, prepareRow, setAllFilters } =
    instance as TableInstance<TaskHistoryItem> & {
      setAllFilters: (filters: any[]) => void;
    };

  // Update parent component with filtered data
  useEffect(() => {
    if (onDataReady && rows) {
      onDataReady(rows);
    }
  }, [onDataReady, rows]);

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  const columnWidths: Record<string, number | string> = {
    noteDate: '20%',
    noteSource: '20%',
    noteDescription: '40%', // Much larger width for Event column
    userId: '20%',
  };

  return (
    <section className='task-table-container-wrapper' ref={containerRef}>
      <div className='table-container task-table-container'>
        <div className='table-header-wrapper'>
          <table {...getTableProps()} className='data-table task-detail-table'>
            <thead>
              {headerGroups.map((headerGroup: HeaderGroup<any>) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((col) => {
                    const column = col as unknown as ColumnInstance<any> & UseSortByColumnProps<any> as any;
                    return (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className={`${column.id}-column`}
                        style={{
                          width: columnWidths[column.id],
                          minWidth:
                            typeof columnWidths[column.id] === 'number' ? `${columnWidths[column.id]}px` : '200px',
                          padding: '8px 12px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div className='flex items-center task-detail'>
                          <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                          {column.isSorted && (
                            <span
                              className={`tdt-sort-icon ${column.isSortedDesc ? 'desc' : 'asc'}`}
                              style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                            />
                          )}
                          <span>{column.canFilter ? column.render('Filter') : null}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          </table>
        </div>
        <div>
          {data.length > 0 ? (
            <div className='virtualized-table-body'>
              {rows.length === 0 ? (
                <div className='text-center p-20 h-[400px] flex items-center justify-center'>
                  <h1 className='text-gray-400'>
                    No data found for the selected filter conditions. Please adjust your filters and try again.
                  </h1>
                </div>
              ) : (
                <FixedSizeList
                  key='notes-history-list'
                  ref={listRef}
                  height={500}
                  itemCount={rows.length}
                  itemSize={45}
                  width={containerWidth}
                >
                  {({ index, style }) => {
                    const row = rows[index];
                    prepareRow(row);
                    return (
                      <div {...row.getRowProps({ style })} className='task-row'>
                        {row.cells.map((cell) => (
                          <div
                            {...cell.getCellProps()}
                            className={`task-cell `}
                            style={{
                              width: columnWidths[cell.column.id],
                              minWidth:
                                typeof columnWidths[cell.column.id] === 'number'
                                  ? `${columnWidths[cell.column.id]}px`
                                  : '200px',
                              flex: 'none',
                              padding: '8px 12px',
                              boxSizing: 'border-box',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: cell.column.id === 'noteDescription' ? 'normal' : 'nowrap',
                            }}
                          >
                            {cell?.value ? cell.render('Cell') : '-'}
                          </div>
                        ))}
                      </div>
                    );
                  }}
                </FixedSizeList>
              )}
            </div>
          ) : (
            <div className='text-center p-20 h-[400px] flex items-center justify-center'>
              <h1 className='text-gray-400'>No notes/tasks data available.</h1>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TaskHistoryTable;
